import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { TriangleAlert, Plus, X } from "lucide-react";

export default function InventoryWastagePage() {
  const { branchId, hasPerm } = useAuth();
  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(BLANK);

  const load = async () => {
    if (!branchId) return;
    try {
      const [i, m, r] = await Promise.all([
        items.length ? Promise.resolve({ data: items }) : api.get("/inventory/items"),
        api.get("/inventory/movements", { params: { branch_id: branchId, type: "wastage", limit: 100 } }),
        reasons.length ? Promise.resolve({ data: reasons }) : api.get("/inventory/wastage-reasons"),
      ]);
      if (!items.length) setItems(i.data);
      setMovements(m.data);
      if (!reasons.length) setReasons(r.data);
    } catch (_) {}
  };
  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, [branchId]);

  const item = useMemo(() => items.find((i) => i.id === form.item_id), [items, form.item_id]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/inventory/movements", {
        branch_id: branchId,
        item_id: form.item_id,
        type: "wastage",
        quantity: parseFloat(form.quantity),
        as_purchase_unit: false,
        reason: form.reason,
        notes: form.notes,
      });
      toast.success("Wastage recorded");
      setAdding(false); setForm(BLANK); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Group by reason for the summary strip
  const byReason = useMemo(() => {
    const m = {};
    for (const w of movements) {
      const r = w.reason || "unspecified";
      const cost = Math.abs(w.quantity_base || 0) * (w.unit_cost || 0);
      m[r] = m[r] || { count: 0, cost: 0 };
      m[r].count++; m[r].cost += cost;
    }
    return Object.entries(m).sort((a, b) => b[1].cost - a[1].cost);
  }, [movements]);

  const total = movements.reduce((s, w) => s + Math.abs(w.quantity_base || 0) * (w.unit_cost || 0), 0);

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="inventory-wastage-page">
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">Inventory · Wastage &amp; spillage</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">Log wastage</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Every drop tracked. Wastage requires a reason and is deducted from stock immediately — variance shows up on the count and the owner report.
          </p>
        </div>
        <div className="text-right">
          <div className="section-eyebrow">Recent wastage cost</div>
          <div className="mt-3 display-heading text-4xl mono ink" data-testid="inv-wastage-total">{peso(total)}</div>
          <div className="mt-1 tiny-label ink-subtle">across {movements.length} events</div>
        </div>
      </div>

      {!branchId && (
        <div className="mt-14 border hairline-soft rounded-md p-8 text-center ink-subtle text-sm">
          Select a branch first.
        </div>
      )}

      {branchId && (
        <>
          {hasPerm("inventory.adjust") && (
            <div className="mt-10 animate-fade-up animate-fade-up-1">
              <button onClick={() => setAdding(true)} data-testid="inv-wastage-new" className="btn-pill btn-pill-solid h-10">
                <Plus size={14}/> Record wastage
              </button>
            </div>
          )}

          {/* By reason */}
          {byReason.length > 0 && (
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-4 animate-fade-up animate-fade-up-2">
              {byReason.slice(0, 4).map(([r, v], i) => (
                <div key={r} data-testid={`inv-wastage-reason-${r}`}>
                  <div className="flex items-center justify-between">
                    <div className="section-eyebrow truncate">{r.replace("_"," ")}</div>
                    <span className="section-num">.0{i+1}</span>
                  </div>
                  <div className="mt-2 mono ink text-xl font-semibold">{peso(v.cost)}</div>
                  <div className="tiny-label ink-subtle mt-1">{v.count} events</div>
                </div>
              ))}
            </div>
          )}

          {/* Ledger */}
          <div className="mt-12 border-y hairline-soft divide-y hairline-soft animate-fade-up animate-fade-up-3" data-testid="inv-wastage-list">
            <div className="grid grid-cols-12 py-3 tiny-label ink-subtle">
              <div className="col-span-4">Item</div>
              <div className="col-span-2">Reason</div>
              <div className="col-span-2 text-right">Qty</div>
              <div className="col-span-2 text-right">Cost</div>
              <div className="col-span-2 text-right">At</div>
            </div>
            {movements.length === 0 && (
              <div className="py-8 ink-subtle text-center text-sm">No wastage logged yet.</div>
            )}
            {movements.map((m) => (
              <div key={m.id} className="grid grid-cols-12 py-3 items-center" data-testid={`inv-wastage-row-${m.id}`}>
                <div className="col-span-4 pr-2 flex items-center gap-2">
                  <TriangleAlert size={12} className="ink-subtle shrink-0"/>
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{m.item_name}</div>
                    <div className="text-xs ink-subtle mono truncate">{m.notes || "—"}</div>
                  </div>
                </div>
                <div className="col-span-2 tiny-label">{(m.reason || "unspecified").replace("_"," ")}</div>
                <div className="col-span-2 text-right mono text-sm text-red-500">{m.quantity_base} {m.base_unit}</div>
                <div className="col-span-2 text-right mono text-sm ink">{peso(Math.abs((m.quantity_base||0)) * (m.unit_cost||0))}</div>
                <div className="col-span-2 text-right mono text-[11px] ink-subtle">
                  {new Date(m.at).toLocaleString("en-PH", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Drawer */}
      {adding && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="inv-wastage-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setAdding(false)}/>
          <form onSubmit={submit} className="relative w-full max-w-md surface-2 border-l hairline h-full p-6 overflow-y-auto shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <div className="section-eyebrow">Inventory · Wastage</div>
                <h2 className="mt-2 display-heading text-2xl ink">Log wastage</h2>
              </div>
              <button type="button" onClick={() => setAdding(false)} className="tiny-label ink-subtle hover:ink"><X size={16}/></button>
            </div>

            <F label="Item">
              <select required value={form.item_id} onChange={(e)=>setForm({...form, item_id: e.target.value})} className="i" data-testid="inv-wf-item">
                <option value="">— Select item —</option>
                {items.map((i) => <option key={i.id} value={i.id}>{i.name} ({i.base_unit})</option>)}
              </select>
            </F>

            <F label={`Quantity wasted (${item?.base_unit || "base unit"})`}>
              <input required autoFocus type="number" step="0.01" value={form.quantity} onChange={(e)=>setForm({...form, quantity: e.target.value})}
                     className="i mono text-lg" data-testid="inv-wf-qty" placeholder="0"/>
            </F>

            <F label="Reason (required)">
              <select required value={form.reason} onChange={(e)=>setForm({...form, reason: e.target.value})} className="i" data-testid="inv-wf-reason">
                <option value="">— Select reason —</option>
                {reasons.map((r) => <option key={r} value={r}>{r.replace("_"," ")}</option>)}
              </select>
            </F>

            <F label="Notes">
              <input value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="i" data-testid="inv-wf-notes"
                     placeholder="e.g. shift 1 — customer refused drink"/>
            </F>

            <div className="mt-6 flex gap-2">
              <button type="button" onClick={() => setAdding(false)} className="btn-pill btn-pill-outline h-11 flex-1">Cancel</button>
              <button type="submit" data-testid="inv-wf-submit" className="btn-pill btn-pill-solid h-11 flex-1"><TriangleAlert size={13}/> Log wastage</button>
            </div>

            <style>{`.i { margin-top: 6px; height: 42px; width: 100%; border-radius: 8px; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); padding: 0 14px; color: hsl(var(--foreground)); font-size: 13px; }`}</style>
          </form>
        </div>
      )}
    </div>
  );
}

const BLANK = { item_id: "", quantity: "", reason: "", notes: "" };

function F({ label, children }) {
  return <label className="block mt-4"><div className="tiny-label">{label}</div>{children}</label>;
}
